import { getFeatureFlagsQueryOptions, setFeatureFlagMutationOptions } from '@gozoki/api'
import { RBAC_ENFORCEMENT_FEATURE_FLAG } from '@gozoki/api-types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Shield, ShieldCheck, type LucideIcon } from 'lucide-react'

import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import { Card, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Label } from '~/components/ui/label'
import { Skeleton } from '~/components/ui/skeleton'
import { Switch } from '~/components/ui/switch'
import {
  useMigratedRight,
  useRbacV2Enabled,
  withRoutePermissions,
  type PermissionRequirement,
} from '~/utils/rbac'

/** Écran 26 — Feature Flags. Ressource sans boutique : aucun périmètre ne s'y applique. */
const FEATURE_FLAG_READ: PermissionRequirement = [
  'read',
  'feature-flags.feature-flags.feature-flag',
]
const FEATURE_FLAG_EDIT: PermissionRequirement = [
  'edit',
  'feature-flags.feature-flags.feature-flag',
]

export const Route = createFileRoute('/_authenticated/feature-flags')({
  staticData: {
    permission: FEATURE_FLAG_READ,
  },
  component: FeatureFlagsPage,
})
withRoutePermissions(Route)

/** Back-office copy + metadata for flags we expose in the UI (extend when adding flags). */
interface FeatureFlagRegistryEntry {
  /** Must match `feature_flags.name` / api-types constants. */
  name: string
  title: string
  description: string
  icon: LucideIcon
}

function featureFlagStatusLabel(isLoading: boolean, enabled: boolean): string {
  if (isLoading) {
    return '…'
  }
  if (enabled) {
    return 'Activé'
  }
  return 'Désactivé'
}

const FEATURE_FLAG_REGISTRY: FeatureFlagRegistryEntry[] = [
  {
    name: RBAC_ENFORCEMENT_FEATURE_FLAG,
    title: 'Appliquer le RBAC v1 (droits par ressource) sur l’API',
    description:
      'Interrupteur maître de l’ancien modèle d’autorisation : activé, les routes protégées sont filtrées selon les permissions v1 des rôles ; désactivé, seuls les droits hérités (bits `admin_rights`) décident. Il ne concerne PAS le modèle v2 par ressource × action, qui s’active compte par compte avec le flag `permission-v2` (voir ci-dessous).',
    icon: Shield,
  },
]

function FeatureFlagsPage() {
  // Tant que `permission-v2` est OFF, le tuple v2 ne discrimine rien : le bit `SUPER_ADMIN` reste
  // la seule garde effective de l'écran, comme avant la bascule.
  const canRead = useMigratedRight(FEATURE_FLAG_READ, ['SUPER_ADMIN'])
  const canEdit = useMigratedRight(FEATURE_FLAG_EDIT, ['SUPER_ADMIN'])
  const { data: flags = [], isLoading } = useQuery({
    ...getFeatureFlagsQueryOptions(),
    enabled: canRead,
  })
  const setFlag = useMutation(setFeatureFlagMutationOptions())

  if (!canRead) {
    return <RbacAccessDenied />
  }

  return (
    <>
      <AppHeader>
        <Title>Feature flags</Title>
      </AppHeader>

      <AppContainer className="w-full min-w-0 space-y-6">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Paramètres runtime</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Activez ou désactivez des comportements sur l’API sans redéployer. Réservé aux
            super-admins.
          </p>
        </div>

        <Card variant="outline" className="gap-0 overflow-hidden p-0">
          <CardHeader className="border-border bg-muted/30 border-b px-5 py-4">
            <CardTitle className="text-sm font-semibold">Liste des indicateurs</CardTitle>
            <CardDescription>
              Chaque interrupteur met à jour la base via l’API ; les changements sont pris en compte
              immédiatement.
            </CardDescription>
          </CardHeader>

          <ul className="divide-border divide-y">
            {FEATURE_FLAG_REGISTRY.map((entry) => {
              const Icon = entry.icon
              const enabled = flags.find((f) => f.name === entry.name)?.enabled ?? false
              const togglingThis = setFlag.isPending && setFlag.variables?.name === entry.name
              const switchDisabled = isLoading || togglingThis || !canEdit
              const statusLabel = featureFlagStatusLabel(isLoading, enabled)

              return (
                <li key={entry.name}>
                  <div className="hover:bg-muted/20 flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-1 gap-3">
                      <div
                        className="text-primary bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-2xl"
                        aria-hidden
                      >
                        <Icon className="size-5" />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <Label htmlFor={`flag-${entry.name}`} className="text-base font-medium">
                          {entry.title}
                        </Label>
                        <p className="text-muted-foreground text-sm leading-snug">
                          {entry.description}
                        </p>
                        <p className="text-muted-foreground/80 font-mono text-xs">{entry.name}</p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3 sm:flex-col sm:items-end">
                      {isLoading ? (
                        <Skeleton className="h-5 w-12 rounded-full" />
                      ) : (
                        <Switch
                          id={`flag-${entry.name}`}
                          size="lg"
                          checked={enabled}
                          disabled={switchDisabled}
                          onCheckedChange={(checked) =>
                            setFlag.mutate({
                              name: entry.name,
                              enabled: checked === true,
                            })
                          }
                        />
                      )}
                      <span className="text-muted-foreground text-xs font-medium tabular-nums">
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>

        <RbacV2AccountCard />
      </AppContainer>
    </>
  )
}

/**
 * État `permission-v2` du compte courant, en lecture seule.
 *
 * Le flag vit dans Flipt (segment de comptes) et non dans la table `feature_flags` : il n'y a rien
 * à basculer ici, et l'écran ne doit pas laisser croire le contraire. Il montre donc la valeur que
 * le serveur annonce dans le contrat de session (`GET /rbac/me/permissions`, la même source que
 * l'ability) et dit où se fait l'activation (audit du 19/09, MAJ-08).
 */
function RbacV2AccountCard() {
  const rbacV2Enabled = useRbacV2Enabled()

  return (
    <Card variant="outline" className="gap-0 overflow-hidden p-0">
      <CardHeader className="border-border bg-muted/30 border-b px-5 py-4">
        <CardTitle className="text-sm font-semibold">
          Autorisations v2 (ressource × action)
        </CardTitle>
        <CardDescription>
          Réglage par compte, hors base : il se pilote dans Flipt (flag <code>permission-v2</code>,
          segment de comptes), pas depuis le back-office.
        </CardDescription>
      </CardHeader>

      <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 gap-3">
          <div
            className="text-primary bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-2xl"
            aria-hidden
          >
            <ShieldCheck className="size-5" />
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-base font-medium">Mon compte</p>
            <p className="text-muted-foreground text-sm leading-snug">
              {rbacV2Enabled
                ? 'Vos accès sont décidés par vos rôles v2 : les droits hérités ne vous ouvrent plus rien.'
                : 'Vos accès sont encore décidés par le régime hérité (bits) et les permissions v1.'}
            </p>
            <p className="text-muted-foreground/80 font-mono text-xs">permission-v2</p>
          </div>
        </div>

        <span className="text-muted-foreground shrink-0 text-xs font-medium tabular-nums">
          {rbacV2Enabled ? 'Activé' : 'Désactivé'}
        </span>
      </div>
    </Card>
  )
}
